import React, { useEffect, useRef, useState } from 'react';
import {
  CircleCheck, Copy, Check, ChevronRight, RefreshCw,
  Globe, Link2Off, Eye, EyeOff, Loader2, Plug,
  Download, Settings, Webhook,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { merchantCredentialsAPI, wordpressConfigAPI } from '../../services/api';

// ─── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:          '#ffffff',
  bgSub:       '#ffffff',
  border:      '#e8e8f1',
  borderSoft:  '#f3f4f6',
  text:        '#14142b',
  textSub:     '#6e6e86',
  textMuted:   '#9ca3af',
  green:       '#16a34a',
  greenBg:     '#f0fdf4',
  greenBorder: '#bbf7d0',
  red:         '#dc2626',
  redBg:       '#fef2f2',
  redBorder:   '#fecaca',
  yellow:      '#d97706',
  yellowBg:    '#fffbeb',
  yellowBorder:'#fde68a',
  blue:        '#7c3aed',
  blueBg:      '#f1ebff',
  blueBorder:  '#bfdbfe',
  purple:      '#7c3aed',
  purpleBg:    '#f5f3ff',
  purpleBorder:'#ddd6fe',
  codebg: '#f4f4f9',
  codeDark:    '#14142b',
};

// ─── Primitives ────────────────────────────────────────────────────────────────
const useClipboard = () => {
  const [copied, setCopied] = useState(null);
  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };
  return { copy, copied };
};

const CopyBtn = ({ text, id, copied, copy }) => (
  <button
    onClick={() => copy(text, id)}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 6,
      border: '1px solid rgba(255,255,255,.15)',
      background: copied === id ? 'rgba(255,255,255,.15)' : 'rgba(255,255,255,.08)',
      color: copied === id ? '#86efac' : '#6e6e86',
      fontSize: 11, fontWeight: 600, cursor: 'pointer',
    }}
  >
    {copied === id ? <Check size={11} /> : <Copy size={11} />}
    {copied === id ? 'Copied' : 'Copy'}
  </button>
);

const CodeBlock = ({ code, lang, label, copied, copy }) => {
  const id = (label || lang || '') + code.slice(0, 20);
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '8px 14px', background: '#1e293b', borderRadius: '8px 8px 0 0',
        borderBottom: '1px solid rgba(255,255,255,.08)',
      }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: '#4a4a6a', textTransform: 'uppercase', letterSpacing: .5 }}>
          {label || lang}
        </span>
        <CopyBtn text={code} id={id} copied={copied} copy={copy} />
      </div>
      <pre style={{
        margin: 0, padding: '16px 18px',
        background: C.codeDark, borderRadius: '0 0 8px 8px',
        fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
        fontSize: 13, lineHeight: 1.7, color: '#e8e8f1',
        overflowX: 'auto', whiteSpace: 'pre',
      }}>{code}</pre>
    </div>
  );
};

const Inline = ({ children }) => (
  <code style={{
    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
    fontSize: '0.875em', background: C.codeBg,
    border: `1px solid ${C.border}`,
    borderRadius: 4, padding: '1px 6px', color: C.text,
  }}>{children}</code>
);

const Callout = ({ icon: Icon, title, children, variant = 'info' }) => {
  const v = {
    info:    { bg: C.blueBg,   border: C.blueBorder,   text: C.blue   },
    success: { bg: C.greenBg,  border: C.greenBorder,  text: C.green  },
    warning: { bg: C.yellowBg, border: C.yellowBorder, text: C.yellow },
    danger:  { bg: C.redBg,    border: C.redBorder,    text: C.red    },
    purple:  { bg: C.purpleBg, border: C.purpleBorder, text: C.purple },
  }[variant];
  return (
    <div style={{
      background: v.bg, border: `1px solid ${v.border}`,
      borderRadius: 8, padding: '12px 16px', marginBottom: 20,
      display: 'flex', gap: 10,
    }}>
      {Icon && <Icon size={15} style={{ color: v.text, flexShrink: 0, marginTop: 1 }} />}
      <div>
        {title && <div style={{ fontWeight: 600, fontSize: 13, color: v.text, marginBottom: 3 }}>{title}</div>}
        <div style={{ fontSize: 13, color: v.text, lineHeight: 1.65 }}>{children}</div>
      </div>
    </div>
  );
};

const SectionTitle = ({ id, children, refEl }) => (
  <h2 id={id} ref={refEl} style={{
    fontSize: 20, fontWeight: 700, color: C.text,
    margin: '0 0 6px', paddingTop: 4, scrollMarginTop: 80,
  }}>{children}</h2>
);

const SectionSub = ({ children }) => (
  <p style={{ fontSize: 14, color: C.textSub, margin: '0 0 20px', lineHeight: 1.6 }}>{children}</p>
);

const Divider = () => (
  <hr style={{ border: 'none', borderTop: `1px solid ${C.borderSoft}`, margin: '32px 0' }} />
);

const StepBadge = ({ n }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: '50%',
    background: C.text, color: '#fff', fontSize: 12, fontWeight: 700,
    flexShrink: 0,
  }}>{n}</span>
);

const StepRow = ({ n, title, children }) => (
  <div style={{ display: 'flex', gap: 14, marginBottom: 22 }}>
    <StepBadge n={n} />
    <div style={{ flex: 1 }}>
      <div style={{ fontWeight: 600, fontSize: 14, color: C.text, marginBottom: 6, paddingTop: 3 }}>{title}</div>
      {children && <div style={{ fontSize: 13.5, color: C.textSub, lineHeight: 1.7 }}>{children}</div>}
    </div>
  </div>
);

const FieldInput = ({ value, onChange, placeholder, type = 'text', readOnly }) => (
  <input
    type={type} value={value} onChange={onChange}
    placeholder={placeholder} readOnly={readOnly}
    style={{
      width: '100%', padding: '9px 12px',
      border: `1px solid ${C.border}`, borderRadius: 8,
      fontSize: 13, outline: 'none', background: readOnly ? C.borderSoft : C.bgSub, color: C.text,
      boxSizing: 'border-box',
    }}
  />
);

// ─── Nav sections ──────────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'overview',    label: 'Overview'              },
  { id: 'how',         label: 'How it works'          },
  { id: 'step1',       label: '1. Download plugin'    },
  { id: 'step2',       label: '2. Install plugin'     },
  { id: 'step3',       label: '3. Configure plugin'   },
  { id: 'step4',       label: '4. Connect site'       },
  { id: 'webhook',     label: '5. Webhook verify'     },
  { id: 'faq',         label: 'FAQ'                   },
];

// ─── Static code snippets ──────────────────────────────────────────────────────

const webhookVerifyCode = `<?php
// In your custom webhook handler (optional — plugin handles this automatically)

$payload   = file_get_contents('php://input');
$sig       = $_SERVER['HTTP_X_SIGNATURE'] ?? '';
$timestamp = $_SERVER['HTTP_X_TIMESTAMP']  ?? '';
$data      = json_decode($payload, true);

// Canonical string format (must match exactly):
$canonical = $data['txn_id']
           . '|' . $data['status']
           . '|' . number_format((float)$data['amount'], 2, '.', '')
           . '|' . $data['merchant_order_id']
           . '|' . $timestamp;

$expected = hash_hmac('sha256', $canonical, YOUR_SECRET_SALT);

if (!hash_equals($expected, $sig)) {
    http_response_code(401);
    exit('Invalid signature');
}

// ✅ Payment verified!
if ($data['status'] === 'success') {
    // WooCommerce plugin updates order automatically.
    // You can add custom logic here if needed.
}`;

// ─── Main component ────────────────────────────────────────────────────────────
export default function WordPressIntegration() {
  const [mid,            setMid]            = useState('');
  const [apiKey,         setApiKey]         = useState('');
  const [loading,        setLoading]        = useState(true);
  const [siteUrl,        setSiteUrl]        = useState('');
  const [inputUrl,       setInputUrl]       = useState('');
  const [saving,         setSaving]         = useState(false);
  const [disconnecting,  setDisconnecting]  = useState(false);
  const [reconnecting,   setReconnecting]   = useState(false);
  const [isConnected,    setIsConnected]    = useState(false);
  const [activeNav,      setActiveNav]      = useState('overview');
  const { copy, copied } = useClipboard();
  const sectionRefs = useRef({});

  // ── Load data on mount ──────────────────────────────────────────────────────
  useEffect(() => {
    Promise.allSettled([
      merchantCredentialsAPI.show(),
      wordpressConfigAPI.get(),
    ]).then(([credRes, cfgRes]) => {
      if (credRes.status === 'fulfilled') {
        const d = credRes.value?.data?.data;
        setMid(d?.merchant_id || '');
        setApiKey(d?.api_key   || '');
      }
      if (cfgRes.status === 'fulfilled') {
        const cfg = cfgRes.value?.data?.data;
        if (cfg?.is_connected) {
          setIsConnected(true);
          setSiteUrl(cfg.wordpress_site_url || '');
        }
      }
    }).finally(() => setLoading(false));
  }, []);

  // ── Intersection observer for left nav ─────────────────────────────────────
  useEffect(() => {
    const obs = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) setActiveNav(e.target.id); }),
      { rootMargin: '-25% 0px -65% 0px' }
    );
    Object.values(sectionRefs.current).forEach(el => { if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, [loading]);

  const scrollTo = (id) => {
    sectionRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setActiveNav(id);
  };

  // ── Save WordPress site URL ─────────────────────────────────────────────────
  const saveConfig = async () => {
    const url = inputUrl.replace(/\/$/, '').trim();
    if (!url) { toast.error('Enter your WordPress site URL.'); return; }
    if (!/^https?:\/\/.+/.test(url)) { toast.error('URL must start with http:// or https://'); return; }

    setSaving(true);
    try {
      await wordpressConfigAPI.save({ wordpress_site_url: url });
      setIsConnected(true);
      setSiteUrl(url);
      setReconnecting(false);
      setInputUrl('');
      toast.success('WordPress site connected!');
    } catch {
      toast.error('Could not save. Try again.');
    } finally { setSaving(false); }
  };

  // ── Disconnect ──────────────────────────────────────────────────────────────
  const disconnect = async () => {
    if (!window.confirm(`Disconnect ${siteUrl}?`)) return;
    setDisconnecting(true);
    try {
      await wordpressConfigAPI.save({ wordpress_site_url: null });
      setIsConnected(false);
      setSiteUrl('');
      toast.success('WordPress site disconnected.');
    } catch {
      toast.error('Could not disconnect. Try again.');
    } finally { setDisconnecting(false); }
  };

  // Plugin download URL
  const pluginDownloadUrl = 'https://api.a1xpay.com/downloads/a1pay-woocommerce.zip';

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 80, color: C.textMuted, fontSize: 14 }}>
      Loading…
    </div>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="doc-layout">

      {/* ── Left sticky nav ──────────────────────────────────────────────────── */}
      <nav className="doc-toc" style={{ position: 'sticky', top: 24, alignSelf: 'flex-start' }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 12 }}>
          On this page
        </div>
        {SECTIONS.map(s => (
          <button key={s.id} onClick={() => scrollTo(s.id)} style={{
            display: 'block', width: '100%', textAlign: 'left',
            padding: '6px 10px', borderRadius: 6, border: 'none',
            background: activeNav === s.id ? C.bgSub : 'transparent',
            color: activeNav === s.id ? C.text : C.textSub,
            fontWeight: activeNav === s.id ? 600 : 400,
            fontSize: 13, cursor: 'pointer', marginBottom: 1,
            borderLeft: `2px solid ${activeNav === s.id ? C.text : 'transparent'}`,
            transition: 'all .1s',
          }}>{s.label}</button>
        ))}
      </nav>

      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minWidth: 0 }}>

        {/* Page header */}
        <div style={{ marginBottom: 32, paddingBottom: 24, borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6 }}>
            Manage Integrations
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: C.text, margin: '0 0 8px' }}>
            WordPress / WooCommerce Setup
          </h1>
          <p style={{ fontSize: 15, color: C.textSub, margin: 0, lineHeight: 1.6 }}>
            Accept payments on your WooCommerce store using A1xPay. Install the plugin,
            enter your API credentials, and go live — no coding required.
          </p>

          {/* Progress badges */}
          <div style={{ display: 'flex', gap: 10, marginTop: 20, flexWrap: 'wrap' }}>
            {[
              { label: 'Plugin downloaded', done: false     },
              { label: 'Plugin installed',  done: false     },
              { label: 'Credentials set',   done: !!apiKey  },
              { label: 'Site connected',    done: isConnected },
            ].map((s, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '5px 12px', borderRadius: 20,
                border: `1px solid ${s.done ? C.greenBorder : C.border}`,
                background: s.done ? C.greenBg : C.bgSub,
              }}>
                {s.done
                  ? <CircleCheck size={12} style={{ color: C.green }} />
                  : <span style={{ width: 12, height: 12, borderRadius: '50%', border: `1.5px solid ${C.textMuted}`, display: 'inline-block' }} />}
                <span style={{ fontSize: 12, fontWeight: 500, color: s.done ? C.green : C.textSub }}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── Overview ─────────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="overview" refEl={el => sectionRefs.current['overview'] = el}>
            Overview
          </SectionTitle>
          <SectionSub>
            A1xPay provides a WooCommerce payment gateway plugin. Your customers see
            "Pay with A1xPay" at checkout and are redirected to the A1xPay payment page.
            After payment, the WooCommerce order is updated automatically via webhook.
          </SectionSub>

          <Callout icon={Globe} title="No Shopify-like registration needed" variant="purple">
            WordPress is open source — no app store, no approval required.
            Just install the plugin and configure your API credentials. Done.
          </Callout>

          {/* Comparison: Shopify vs WordPress */}
          <div style={{
            border: `1px solid ${C.border}`, borderRadius: 10,
            overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', background: C.text, padding: '10px 16px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: .6 }}>Feature</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .6 }}>Shopify</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#86efac', textTransform: 'uppercase', letterSpacing: .6 }}>WordPress</span>
            </div>
            {[
              ['Setup',          'OAuth App Registration', 'Just install plugin'],
              ['API Auth',       'Access Token (OAuth)',   'API Key + Secret'],
              ['Order creation', 'Auto via Shopify API',  'WooCommerce native'],
              ['Webhook',        'Shopify webhook URL',   'WooCommerce API URL'],
              ['Store connect',  'Required (OAuth)',      'Optional (cosmetic)'],
            ].map(([f, sh, wp], i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
                padding: '10px 16px', background: i % 2 === 0 ? C.bg : C.bgSub,
                borderTop: `1px solid ${C.borderSoft}`,
              }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{f}</span>
                <span style={{ fontSize: 13, color: C.textSub }}>{sh}</span>
                <span style={{ fontSize: 13, color: C.green, fontWeight: 500 }}>{wp}</span>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── How it works ─────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="how" refEl={el => sectionRefs.current['how'] = el}>
            How it works
          </SectionTitle>
          <SectionSub>End-to-end payment flow from WooCommerce checkout to order completion.</SectionSub>

          <div style={{ marginBottom: 24 }}>
            {[
              'Customer goes to WooCommerce checkout — selects "Pay with A1xPay".',
              'Clicks "Place Order" — WooCommerce creates a pending order.',
              'Plugin calls A1xPay API — gets a checkout URL.',
              'Customer is redirected to A1xPay payment page — pays.',
              'A1xPay sends webhook to your WooCommerce site — order marked "Processing".',
              'Customer is redirected to WooCommerce order confirmation page.',
              'Merchant ships the product from WooCommerce Orders panel.',
            ].map((text, i) => (
              <div key={i} style={{ display: 'flex', gap: 14, marginBottom: 14 }}>
                <div style={{
                  width: 26, height: 26, borderRadius: '50%',
                  background: i === 4 ? C.green : C.text,
                  color: '#fff', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0,
                }}>{i + 1}</div>
                <div style={{ paddingTop: 3, fontSize: 14, color: C.textSub, lineHeight: 1.5 }}>
                  {text}
                </div>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── Step 1: Download plugin ───────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step1" refEl={el => sectionRefs.current['step1'] = el}>
            1. Download the A1xPay Plugin
          </SectionTitle>
          <SectionSub>
            Download the WooCommerce payment gateway plugin. It's a .zip file — no signup needed.
          </SectionSub>

          <div style={{
            border: `1px solid ${C.blueBorder}`, borderRadius: 14,
            padding: '24px 28px', background: C.blueBg,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 20, marginBottom: 24,
          }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.blue, marginBottom: 4 }}>
                a1pay-woocommerce.zip
              </div>
              <div style={{ fontSize: 13, color: C.blue, opacity: .8 }}>
                WooCommerce Payment Gateway Plugin · PHP · v1.0.0
              </div>
            </div>
            <a
              href={pluginDownloadUrl}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 24px', borderRadius: 8,
                background: C.blue, color: '#fff',
                fontSize: 14, fontWeight: 600, textDecoration: 'none',
                border: 'none', cursor: 'pointer',
              }}
            >
              <Download size={15} /> Download Plugin
            </a>
          </div>

          <Callout icon={ChevronRight} variant="info" title="Alternatively">
            If the download link doesn't work yet, ask your A1xPay account manager for the plugin .zip file.
            You can also get it from <strong>A1xPay Dashboard → Downloads</strong> when available.
          </Callout>
        </section>

        <Divider />

        {/* ── Step 2: Install plugin ────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step2" refEl={el => sectionRefs.current['step2'] = el}>
            2. Install the Plugin on WordPress
          </SectionTitle>
          <SectionSub>
            Upload and activate the plugin from your WordPress Admin dashboard.
          </SectionSub>

          <StepRow n="1" title="Open your WordPress Admin">
            Go to <strong>yoursite.com/wp-admin</strong> and log in as Administrator.
          </StepRow>

          <StepRow n="2" title={<span>Go to <strong>Plugins → Add New Plugin</strong></span>}>
            In the left sidebar: <strong>Plugins → Add New Plugin → Upload Plugin</strong>
          </StepRow>

          <StepRow n="3" title='Click "Upload Plugin" → Choose file'>
            Select the <Inline>a1pay-woocommerce.zip</Inline> file you downloaded.
          </StepRow>

          <StepRow n="4" title='Click "Install Now" → then "Activate Plugin"'>
            Once activated, the plugin appears in your installed plugins list.
          </StepRow>

          <Callout icon={CircleCheck} variant="success" title="WooCommerce required">
            WooCommerce must be installed and activated before A1xPay plugin.
            If not installed: <strong>Plugins → Add New → search "WooCommerce" → Install → Activate</strong>.
          </Callout>
        </section>

        <Divider />

        {/* ── Step 3: Configure plugin ──────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step3" refEl={el => sectionRefs.current['step3'] = el}>
            3. Configure Plugin Credentials
          </SectionTitle>
          <SectionSub>
            Enter your A1xPay credentials in WooCommerce payment settings.
          </SectionSub>

          <StepRow n="1" title={<span>Go to <strong>WooCommerce → Settings → Payments</strong></span>}>
            You will see <strong>"A1xPay Payment Gateway"</strong> listed. Click <strong>Manage</strong>.
          </StepRow>

          <StepRow n="2" title="Fill in your A1xPay credentials:">
            <div style={{ marginTop: 8 }}>
              {/* Credentials Table */}
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 16 }}>
                <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', background: C.text, padding: '8px 14px' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: .5 }}>Field</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>Value</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>Copy</span>
                </div>

                {[
                  {
                    field: 'Merchant ID',
                    value: mid || '(loading…)',
                    key:   'mid',
                    hint:  'Your unique merchant identifier',
                  },
                  {
                    field: 'API Key',
                    value: apiKey || '(loading…)',
                    key:   'apikey',
                    hint:  'Used to authenticate API requests',
                  },
                  {
                    field: 'Secret Salt',
                    value: '●●●●●●●●●●●●●●●●',
                    key:   null,
                    hint:  'Reveal from API Setup → Credentials → Reveal Secret',
                    noopy: true,
                  },
                ].map((row, i) => (
                  <div key={i} style={{
                    display: 'grid', gridTemplateColumns: '180px 1fr auto',
                    padding: '12px 14px', background: i % 2 === 0 ? C.bg : C.bgSub,
                    borderTop: `1px solid ${C.borderSoft}`, alignItems: 'center',
                  }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{row.field}</div>
                      <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>{row.hint}</div>
                    </div>
                    <code style={{
                      fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
                      fontSize: 12, color: row.noopy ? C.textMuted : C.text,
                      wordBreak: 'break-all',
                    }}>
                      {row.value}
                    </code>
                    <div>
                      {!row.noopy && row.key && (
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(row.value);
                            toast.success(`${row.field} copied!`);
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '5px 10px', borderRadius: 6,
                            border: `1px solid ${C.border}`, background: C.bgSub,
                            color: C.textSub, fontSize: 11, cursor: 'pointer',
                          }}
                        >
                          {copied === row.key ? <Check size={11} /> : <Copy size={11} />}
                          Copy
                        </button>
                      )}
                      {row.noopy && (
                        <a
                          href="/user/integration"
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '5px 10px', borderRadius: 6,
                            border: `1px solid ${C.blueBorder}`, background: C.blueBg,
                            color: C.blue, fontSize: 11, textDecoration: 'none',
                          }}
                        >
                          <Eye size={11} /> Reveal
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </StepRow>

          <StepRow n="3" title='Set "Environment" to Live (after testing)'>
            Use <strong>Test Mode</strong> first to verify the integration, then switch to <strong>Live</strong>.
          </StepRow>

          <StepRow n="4" title='Enable the payment method → Click "Save changes"'>
            Toggle "Enable A1xPay Payment Gateway" to ON, then save.
          </StepRow>

          <Callout icon={Settings} title="Where to find Secret Salt?" variant="warning">
            Go to <strong>API Setup → Credentials → Reveal Secret Salt</strong> in the A1xPay dashboard.
            Copy it immediately — it's shown only once. Paste it in the WooCommerce plugin settings.
          </Callout>
        </section>

        <Divider />

        {/* ── Step 4: Connect site ──────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="step4" refEl={el => sectionRefs.current['step4'] = el}>
            4. Connect your WordPress site (Optional)
          </SectionTitle>
          <SectionSub>
            Save your WordPress site URL here for tracking purposes. This is optional —
            payments work without this step.
          </SectionSub>

          {/* Connected state */}
          {isConnected && !reconnecting ? (
            <div style={{
              border: `1px solid ${C.greenBorder}`, background: C.greenBg,
              borderRadius: 14, padding: '20px 24px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 16, marginBottom: 24,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 42, height: 42, borderRadius: '50%', background: C.green,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Globe size={19} color="#fff" />
                </div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green, marginBottom: 2 }}>
                    WordPress Site Saved ✓
                  </div>
                  <div style={{
                    fontSize: 13, color: C.green, opacity: .8,
                    fontFamily: '"JetBrains Mono","Fira Code",Menlo,monospace',
                  }}>
                    {siteUrl}
                  </div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => { setReconnecting(true); setInputUrl(siteUrl); }}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${C.greenBorder}`,
                    background: C.bg, color: C.green,
                    fontSize: 13, fontWeight: 500, cursor: 'pointer',
                  }}
                >
                  <RefreshCw size={13} /> Update
                </button>
                <button
                  onClick={disconnect}
                  disabled={disconnecting}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '8px 16px', borderRadius: 8,
                    border: `1px solid ${C.redBorder}`,
                    background: C.redBg, color: C.red,
                    fontSize: 13, fontWeight: 500,
                    cursor: disconnecting ? 'not-allowed' : 'pointer',
                  }}
                >
                  <Link2Off size={13} /> {disconnecting ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          ) : null}

          {/* Connect form */}
          {(!isConnected || reconnecting) && (
            <div style={{
              border: `1px solid ${C.border}`, borderRadius: 14,
              padding: 24, background: C.bgSub, marginBottom: 20,
            }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: C.text, marginBottom: 4 }}>
                Enter your WordPress site URL
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>
                This helps A1xPay identify which site is integrated with your account.
              </div>

              <div style={{ marginBottom: 16 }}>
                <FieldInput
                  value={inputUrl}
                  onChange={e => setInputUrl(e.target.value)}
                  placeholder="https://yourstore.com"
                />
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>
                  Must start with https:// — use your actual domain, not wp-admin URL.
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <button
                  onClick={saveConfig}
                  disabled={saving || !inputUrl.trim()}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '10px 22px', borderRadius: 8, border: 'none',
                    background: saving || !inputUrl.trim() ? C.bgSub : C.text,
                    color: saving || !inputUrl.trim() ? C.textMuted : '#fff',
                    fontSize: 14, fontWeight: 600,
                    cursor: saving || !inputUrl.trim() ? 'not-allowed' : 'pointer',
                    transition: 'all .15s',
                  }}
                >
                  {saving
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Saving…</>
                    : <><Plug size={14} /> Save Site</>
                  }
                </button>
                {reconnecting && (
                  <button
                    onClick={() => setReconnecting(false)}
                    style={{
                      padding: '10px 16px', borderRadius: 8,
                      border: `1px solid ${C.border}`, background: C.bg,
                      color: C.textSub, fontSize: 13, cursor: 'pointer',
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          )}

          <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
        </section>

        <Divider />

        {/* ── Webhook Verify ────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="webhook" refEl={el => sectionRefs.current['webhook'] = el}>
            5. Webhook Verification (Reference)
          </SectionTitle>
          <SectionSub>
            The WooCommerce plugin handles webhooks automatically. This code is for reference
            only — if you want to add custom logic when a payment is received.
          </SectionSub>

          <Callout icon={Webhook} title="Automatic handling" variant="success">
            The A1xPay plugin registers <Inline>/?wc-api=a1pay_gateway</Inline> automatically.
            When A1xPay sends a webhook, the plugin verifies the signature and updates the order status.
            You don't need to write any code.
          </Callout>

          <CodeBlock
            code={webhookVerifyCode}
            lang="php"
            label="Webhook signature verification (reference)"
            copied={copied}
            copy={copy}
          />

          {/* Webhook payload structure */}
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
            Webhook payload sent by A1xPay:
          </div>
          <div style={{
            border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 20,
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '180px 120px 1fr', background: C.text, padding: '8px 14px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: .5 }}>Field</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>Type</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>Description</span>
            </div>
            {[
              ['txn_id',            'string',  'A1xPay transaction ID (e.g. TXN_20260526_ABCD)'],
              ['merchant_order_id', 'string',  'Your WooCommerce order ID (e.g. wc_123)'],
              ['amount',            'float',   'Payment amount in INR'],
              ['currency',          'string',  'Always "INR"'],
              ['status',            'string',  '"success" | "failed" | "pending"'],
              ['merchant_fee',      'float',   'A1xPay platform fee'],
              ['net_amount',        'float',   'Amount credited to your wallet'],
              ['gateway_txn_id',    'string',  'Payment gateway transaction ID'],
              ['completed_at',      'string',  'ISO 8601 timestamp of completion'],
            ].map(([f, t, d], i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '180px 120px 1fr',
                padding: '10px 14px', background: i % 2 === 0 ? C.bg : C.bgSub,
                borderTop: `1px solid ${C.borderSoft}`,
              }}>
                <code style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 12, color: C.blue }}>{f}</code>
                <span style={{ fontSize: 12, color: C.textMuted }}>{t}</span>
                <span style={{ fontSize: 13, color: C.textSub }}>{d}</span>
              </div>
            ))}
          </div>

          {/* Headers */}
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 }}>
            HTTP Headers sent with webhook:
          </div>
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', marginBottom: 24 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', background: C.text, padding: '8px 14px' }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', textTransform: 'uppercase', letterSpacing: .5 }}>Header</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#6e6e86', textTransform: 'uppercase', letterSpacing: .5 }}>Value</span>
            </div>
            {[
              ['X-Signature',     'HMAC-SHA256 signature of canonical string'],
              ['X-Timestamp',     'Unix timestamp (seconds)'],
              ['X-A1xPay-Event',  '"payment.success" | "payment.failed"'],
            ].map(([h, v], i) => (
              <div key={i} style={{
                display: 'grid', gridTemplateColumns: '200px 1fr',
                padding: '10px 14px', background: i % 2 === 0 ? C.bg : C.bgSub,
                borderTop: `1px solid ${C.borderSoft}`,
              }}>
                <code style={{ fontFamily: '"JetBrains Mono",monospace', fontSize: 12, color: C.blue }}>{h}</code>
                <span style={{ fontSize: 13, color: C.textSub }}>{v}</span>
              </div>
            ))}
          </div>
        </section>

        <Divider />

        {/* ── FAQ ───────────────────────────────────────────────────────────────── */}
        <section>
          <SectionTitle id="faq" refEl={el => sectionRefs.current['faq'] = el}>
            FAQ
          </SectionTitle>
          <SectionSub>Common questions about WordPress / WooCommerce integration.</SectionSub>

          {[
            {
              q: 'Do I need a Shopify or WordPress account on A1xPay?',
              a: 'No. WordPress integration requires no registration on any third-party platform. Just install the plugin and configure your A1xPay credentials.',
            },
            {
              q: 'What happens if I don\'t set the webhook URL?',
              a: 'The plugin automatically generates the webhook URL as: yoursite.com/?wc-api=a1pay_gateway. A1xPay sends payment confirmations to this URL automatically — no manual configuration needed.',
            },
            {
              q: 'Will WooCommerce orders be created automatically?',
              a: 'Yes. When the customer places an order in WooCommerce and pays via A1xPay, the WooCommerce order already exists (created when customer clicks "Place Order"). After payment, the plugin updates the order status to "Processing" automatically.',
            },
            {
              q: 'Can I use both Shopify and WordPress integrations simultaneously?',
              a: 'Yes. They are completely separate integrations. Each uses different webhook URLs and merchant order ID prefixes, so there\'s no conflict.',
            },
            {
              q: 'What is the merchant_order_id format for WooCommerce?',
              a: 'The plugin uses "wc_{order_id}" format — e.g., wc_123. This lets the webhook handler look up the correct WooCommerce order to update.',
            },
            {
              q: 'Does A1xPay create WooCommerce orders like it does for Shopify?',
              a: 'No — and that\'s the advantage! In WooCommerce, the order is already created by WooCommerce when the customer checks out. A1xPay only needs to update the order status after payment, which is simpler and more reliable.',
            },
          ].map(({ q, a }, i, arr) => (
            <div key={i} style={{
              marginBottom: 20, paddingBottom: 20,
              borderBottom: i < arr.length - 1 ? `1px solid ${C.borderSoft}` : 'none',
            }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 6 }}>{q}</div>
              <div style={{ fontSize: 13.5, color: C.textSub, lineHeight: 1.7 }}>{a}</div>
            </div>
          ))}
        </section>

        <div style={{ height: 64 }} />
        <style>{`
          .doc-layout { max-width: 1100px; margin: 0 auto; display: flex; gap: 48px; align-items: flex-start; }
          .doc-toc { width: 200px; flex-shrink: 0; }
          @media (max-width: 768px) { .doc-toc { display: none; } }
        `}</style>
      </div>
    </div>
  );
}
